/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Fixed Google Calendar brand colours; these hex values are the product's brand palette, not themeable design tokens. */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- `mask-type` is an SVG presentation attribute applied via the style prop; there is no css-prop equivalent for a raw <mask> element. */
/* eslint-disable @atlaskit/platform/one-value-export-per-file -- The embeddable content variant (GoogleCalendarIconContent) is intentionally co-located with its standalone wrapper (GoogleCalendarIcon). */
import React, { memo, useId } from 'react';

export type GoogleCalendarIconProps = {
	/** Accessible label; omit for decorative use. */
	label?: string;
	size?: number | string;
	testId?: string;
};

/**
 * Inner SVG content for the Google Calendar icon (paths + defs), with no `<svg>`
 * wrapper. Render this directly inside a parent `<svg>` when embedding the icon in
 * a larger illustration — it avoids nesting a full `<svg>` (and its filters/masks)
 * inside another SVG, which resolves filter/mask id references inconsistently in
 * some browsers. IDs are generated per-instance via `useId`, so multiple instances
 * within the same document never collide.
 *
 * Draws in a 29×29 user-space; position and scale it via a parent `<g transform>`.
 * For standalone use, prefer {@link GoogleCalendarIcon}.
 */
export const GoogleCalendarIconContent = (): React.JSX.Element => {
	const id = useId().replace(/:/g, '');
	const clipId = `new-google-calendar-clip-${id}`;
	const bodyMaskId = `new-google-calendar-body-mask-${id}`;
	const highlightMaskId = `new-google-calendar-highlight-mask-${id}`;
	const highlightGradientId = `new-google-calendar-highlight-gradient-${id}`;
	const topGradientId = `new-google-calendar-top-gradient-${id}`;
	const topBlurId = `new-google-calendar-top-blur-${id}`;

	return (
		<>
			<g clipPath={`url(#${clipId})`}>
				<g transform="rotate(-7.01 14.49 14.49)">
					<path
						d="M6.86583 5.47534C7.1287 3.3375 9.07481 1.81759 11.2126 2.08046L20.6747 3.24393C22.8125 3.5068 24.3325 5.45291 24.0696 7.59074L23.5672 11.6766C23.3043 13.8145 21.3582 15.3344 19.2204 15.0715L9.7583 13.908C7.62047 13.6452 6.10055 11.6991 6.36342 9.56123L6.86583 5.47534Z"
						fill="#BBE2FF"
					/>
					<path
						d="M5.0271 6.96783C5.00906 4.84102 6.8667 3.18389 8.97753 3.44344L22.5134 5.10783C24.6244 5.3674 26.0251 7.42518 25.4923 9.48427L23.8977 15.6472L23.9518 22.0129C23.9698 24.1397 22.1123 25.7969 20.0014 25.5373L6.46548 23.8729C4.35453 23.6133 2.95381 21.5556 3.48656 19.4965L5.08113 13.3335L5.0271 6.96783Z"
						fill="#3C90FF"
					/>
					<mask
						id={bodyMaskId}
						maskUnits="userSpaceOnUse"
						x="3"
						y="3"
						width="23"
						height="23"
						style={{ maskType: 'alpha' }}
					>
						<path
							d="M5.0271 6.96783C5.00906 4.84102 6.8667 3.18389 8.97753 3.44344L22.5134 5.10783C24.6244 5.3674 26.0251 7.42518 25.4923 9.48427L23.8977 15.6472L23.9518 22.0129C23.9698 24.1397 22.1123 25.7969 20.0014 25.5373L6.46548 23.8729C4.35453 23.6133 2.95381 21.5556 3.48656 19.4965L5.08113 13.3335L5.0271 6.96783Z"
							fill="#3C90FF"
						/>
					</mask>
					<g mask={`url(#${bodyMaskId})`}>
						<path
							d="M2.07861 23.333L24.3897 26.0764L25.6458 15.8617L3.33463 13.1183L2.07861 23.333Z"
							fill={`url(#${highlightGradientId})`}
						/>
					</g>
					<mask
						id={highlightMaskId}
						maskUnits="userSpaceOnUse"
						x="3"
						y="3"
						width="23"
						height="23"
						style={{ maskType: 'alpha' }}
					>
						<path
							d="M5.0271 6.96783C5.00906 4.84102 6.8667 3.18389 8.97753 3.44344L22.5134 5.10783C24.6244 5.3674 26.0251 7.42518 25.4923 9.48427L23.8977 15.6472L23.9518 22.0129C23.9698 24.1397 22.1123 25.7969 20.0014 25.5373L6.46548 23.8729C4.35453 23.6133 2.95381 21.5556 3.48656 19.4965L5.08113 13.3335L5.0271 6.96783Z"
							fill="#3186FF"
						/>
					</mask>
					<g mask={`url(#${highlightMaskId})`}>
						<g filter={`url(#${topBlurId})`}>
							<path
								d="M7.02449 4.18603C7.19973 2.76081 8.49714 1.74753 9.92236 1.92278L21.965 3.40356C23.3902 3.57881 24.4035 4.87621 24.2282 6.30144L23.0912 15.5485L5.88746 13.4331L7.02449 4.18603Z"
								fill={`url(#${topGradientId})`}
							/>
						</g>
					</g>
					<path
						d="M11.0966 19.1675C10.5337 19.0983 10.0622 18.9474 9.6819 18.7148C9.30165 18.4822 8.99081 18.1955 8.7494 17.8545C8.51304 17.5095 8.35594 17.1812 8.27808 16.8698C8.20023 16.5583 8.17671 16.3719 8.20754 16.3106C8.2406 16.2477 8.29629 16.1998 8.36334 16.1764L9.16255 15.9681C9.23046 15.944 9.29509 15.9473 9.35644 15.978C9.41816 16.0041 9.48072 16.1187 9.54409 16.3216C9.61203 16.525 9.71512 16.7444 9.85335 16.9798C9.9927 17.2069 10.1774 17.4028 10.3959 17.5552C10.6148 17.7029 10.8957 17.7978 11.2388 17.84C11.7926 17.9081 12.2516 17.802 12.616 17.5216C12.9849 17.2418 13.1997 16.8547 13.2605 16.3605C13.3263 15.825 13.203 15.3917 12.8907 15.0606C12.5789 14.725 12.137 14.5219 11.5649 14.4516L10.8442 14.363C10.7779 14.356 10.717 14.3235 10.6742 14.2725C10.6346 14.2165 10.6188 14.1565 10.6266 14.0924L10.7169 13.3579C10.7253 13.2892 10.7552 13.2348 10.8065 13.1947C10.8326 13.1734 10.8626 13.1577 10.895 13.1485C10.9273 13.1393 10.9611 13.1368 10.9945 13.1411L11.619 13.2179C12.1316 13.2809 12.5607 13.192 12.9062 12.951C13.2518 12.7101 13.4518 12.3677 13.5064 11.9238C13.5604 11.4846 13.4644 11.1128 13.2184 10.8084C12.9724 10.504 12.6046 10.3217 12.115 10.2615C11.8404 10.2277 11.5968 10.2442 11.3842 10.311C11.174 10.3764 10.9801 10.4855 10.8152 10.6313C10.6529 10.7695 10.5054 10.9242 10.3752 11.0929C10.2486 11.2539 10.1521 11.3396 10.0857 11.35C10.02 11.3558 9.95965 11.3368 9.90455 11.2929L9.22679 10.8541C9.17124 10.8148 9.13855 10.7596 9.12871 10.6887C9.11887 10.6178 9.19349 10.4644 9.35257 10.2285C9.51678 9.98855 9.7502 9.75243 10.0528 9.52013C10.3566 9.28567 10.7047 9.11513 11.0762 9.01877C11.4558 8.91673 11.8858 8.89529 12.3663 8.95447C13.2588 9.06421 13.9369 9.3868 14.4006 9.92226C14.8649 10.4532 15.0498 11.1032 14.9553 11.8721C14.89 12.403 14.7053 12.8471 14.4012 13.2046C14.1016 13.5625 13.7109 13.7932 13.229 13.8968L13.2256 13.9242C13.7637 14.1576 14.1659 14.5136 14.4322 14.9923C14.7037 15.4671 14.8023 16.0065 14.7279 16.6105C14.6216 17.4755 14.2323 18.1478 13.5601 18.6273C12.8881 19.1067 12.067 19.2868 11.0966 19.1675ZM18.0043 19.8566C17.9265 19.847 17.8615 19.8088 17.8092 19.742C17.7621 19.6779 17.742 19.5979 17.7531 19.5191L18.6917 11.8855L17.0104 12.8076C16.9503 12.8421 16.8845 12.848 16.813 12.8252C16.7485 12.805 16.6942 12.761 16.661 12.702L16.2925 12.0155C16.2591 11.9551 16.2504 11.8841 16.268 11.8174C16.2907 11.7459 16.3343 11.6933 16.3989 11.6594L19.3785 10.0396C19.4037 10.0242 19.4306 10.0136 19.4591 10.0078C19.4883 9.99747 19.5212 9.99455 19.5577 9.99905L20.1345 10.07C20.2122 10.0795 20.2706 10.1146 20.3097 10.1751C20.3539 10.2315 20.3712 10.2987 20.3616 10.3766L19.2153 19.6989C19.2051 19.7814 19.1667 19.8486 19.0999 19.9007C19.0704 19.9264 19.0358 19.9454 18.9984 19.9564C18.9609 19.9674 18.9215 19.9702 18.8829 19.9646L18.0043 19.8566Z"
						fill="white"
					/>
				</g>
			</g>
			<defs>
				<filter
					id={topBlurId}
					x="4.2627"
					y="0.27832"
					width="21.6104"
					height="16.8955"
					filterUnits="userSpaceOnUse"
					colorInterpolationFilters="sRGB"
				>
					<feFlood floodOpacity="0" result="BackgroundImageFix" />
					<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
					<feGaussianBlur stdDeviation="0.8125" result="effect1_foregroundBlur" />
				</filter>
				<linearGradient
					id={highlightGradientId}
					x1="14.4902"
					y1="14.49"
					x2="13.2342"
					y2="24.7047"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#4FA0FF" />
					<stop offset="1" stopColor="#3186FF" />
				</linearGradient>
				<linearGradient
					id={topGradientId}
					x1="14.7837"
					y1="4.39654"
					x2="13.5501"
					y2="14.4285"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#A9A8FF" />
					<stop offset="0.8" stopColor="#3C90FF" />
				</linearGradient>
				<clipPath id={clipId}>
					<rect width="29" height="29" fill="white" />
				</clipPath>
			</defs>
		</>
	);
};

const GoogleCalendarIconBase = ({
	label,
	size = 29,
	testId,
}: GoogleCalendarIconProps): React.JSX.Element => {
	const isLabelled = typeof label === 'string' && label.length > 0;

	const a11yProps = isLabelled
		? ({ role: 'img', 'aria-label': label } as const)
		: ({ role: 'presentation', 'aria-hidden': true } as const);

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 29 29"
			fill="none"
			data-testid={testId}
			{...a11yProps}
		>
			<GoogleCalendarIconContent />
		</svg>
	);
};

export const GoogleCalendarIcon: React.MemoExoticComponent<typeof GoogleCalendarIconBase> =
	memo(GoogleCalendarIconBase);
